import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Check, Lock, ChevronRight, AlertTriangle, Flag } from "lucide-react";

const GATE_STYLES = {
  green: "bg-emerald-100 border-emerald-300 text-emerald-800",
  amber: "bg-amber-100 border-amber-300 text-amber-800",
  red: "bg-red-100 border-red-300 text-red-700",
};

export default function TrainingCamp() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/training/camp").then((r) => setData(r.data)).catch(() => toast.error("Could not load your training camp"));
  }, []);

  if (!data) return <div className="h-8 w-8 border-4 border-steel border-t-lomond rounded-full animate-spin" data-testid="camp-loading" />;
  const { modules, days, current_day, stats, week1_review, final_theory, final_gate, external, trainee_name } = data;

  const statTile = (label, value, tid) => (
    <div className="bg-white border border-steel rounded-md p-3.5 text-center">
      <p className="font-heading font-black text-2xl text-charcoal" data-testid={tid}>{value}</p>
      <p className="text-[9px] font-mono uppercase tracking-widest text-slate-500 mt-1">{label}</p>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="training-camp-page">
      <div>
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-lomond mb-1">Lomond Operator Training Camp</p>
        <h1 className="font-heading font-extrabold uppercase tracking-tight text-charcoal text-2xl sm:text-3xl" data-testid="camp-heading">
          {trainee_name} — Day {current_day} of 10
        </h1>
        <p className="text-sm text-slate-500 mt-1">Days unlock as you complete them: theory, quiz, practical and instructor sign-off.</p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="camp-stats">
        {statTile("Current day", `${current_day}/10`, "stat-current-day")}
        {statTile("Theory average", stats.theory_avg != null ? `${stats.theory_avg}%` : "—", "stat-theory-avg")}
        {statTile("Days present", stats.attendance.present ?? 0, "stat-attendance")}
        {statTile("Practical days", `${stats.practical_done}/10`, "stat-practical")}
        {statTile("Safety concerns", stats.safety_concerns, "stat-safety")}
      </div>

      {week1_review && (
        <div className={`rounded-md border p-4 flex items-start gap-3 ${GATE_STYLES[week1_review.decision] || ""}`} data-testid="week1-chip">
          <Flag className="h-5 w-5 shrink-0 mt-0.5" />
          <div>
            <p className="font-heading font-bold uppercase text-sm">
              Week 1 Review: {week1_review.decision === "green" ? "Green — continue to Week 2" : week1_review.decision === "amber" ? "Amber — continue with additional development" : "Red — management review"}
            </p>
            {week1_review.strengths && <p className="text-sm mt-1">Strengths: {week1_review.strengths}</p>}
            {week1_review.development_areas && <p className="text-sm mt-0.5">Development areas: {week1_review.development_areas}</p>}
            {week1_review.corrective_action && <p className="text-sm mt-0.5">Corrective action: {week1_review.corrective_action} (review {week1_review.review_date})</p>}
          </div>
        </div>
      )}

      {final_gate && (
        <div className="rounded-md border border-lomond bg-lomond/15 p-4" data-testid="final-gate-chip">
          <p className="font-heading font-bold uppercase text-charcoal text-sm">Provider gate: {final_gate.decision_label}</p>
          <p className="text-sm text-slate-600 mt-0.5">{final_gate.reason}</p>
          {final_gate.decision === "approved_external" && (
            <p className="text-xs text-slate-500 mt-1">You remain a Lomond Trainee until you successfully complete the external 360 training and Lomond approves your progression.</p>
          )}
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-3" data-testid="camp-days">
        {modules.map((m) => {
          const rec = days[String(m.day)] || {};
          const done = rec.completed;
          const unlocked = rec.unlocked;
          const current = m.day === current_day && !done;
          return (
            <button key={m.day} type="button" disabled={!unlocked}
              onClick={() => navigate(`/training/day/${m.day}`)}
              className={`text-left rounded-md border p-4 sm:p-5 transition-all ${
                done ? "bg-emerald-50 border-emerald-300"
                : current ? "bg-white border-lomond border-2 shadow-sm"
                : unlocked ? "bg-white border-steel hover:border-lomond"
                : "bg-elevated border-steel opacity-70 cursor-not-allowed"
              }`}
              data-testid={`day-card-${m.day}`}>
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <span className={`h-9 w-9 rounded-full flex items-center justify-center shrink-0 font-heading font-black ${
                    done ? "bg-emerald-500 text-white" : current ? "bg-lomond text-black" : unlocked ? "bg-elevated text-charcoal border border-steel" : "bg-steel/40 text-slate-400"
                  }`}>
                    {done ? <Check className="h-5 w-5" /> : unlocked ? m.day : <Lock className="h-4 w-4" />}
                  </span>
                  <div className="min-w-0">
                    <p className="font-heading font-bold uppercase text-charcoal text-sm leading-tight">{m.title}</p>
                    <p className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mt-0.5">
                      {done ? "Complete" : current ? "In progress" : unlocked ? "Open" : "Locked"}
                      {rec.quiz_best_pct != null && m.day !== 10 ? ` — quiz best ${rec.quiz_best_pct}%` : ""}
                      {m.day === 10 && final_theory?.status ? ` — theory ${final_theory.status.replace(/_/g, " ")}` : ""}
                    </p>
                  </div>
                </div>
                {unlocked && <ChevronRight className="h-4 w-4 text-slate-400 shrink-0" />}
              </div>
            </button>
          );
        })}
      </div>

      {external && (
        <div className="rounded-md border border-steel bg-white p-5" data-testid="external-card">
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-lomond mb-2">External 360 Training</p>
          <p className="font-heading font-bold uppercase text-charcoal" data-testid="external-course">{external.course_name}</p>
          <p className="text-sm text-slate-600 mt-1" data-testid="external-dates">{external.provider} — {external.start_date} to {external.end_date}</p>
          <p className="text-sm mt-2" data-testid="external-status">
            {external.status === "booked" && <span className="text-sky-700 font-semibold">Booked — attend the provider on these dates. Lomond is tracking your place.</span>}
            {external.status === "completed" && <span className="text-emerald-700 font-semibold">Result recorded as a PASS — Lomond management is reviewing your certificate.</span>}
            {external.status === "failed" && <span className="text-amber-700 font-semibold">Result recorded as not passed this time — Lomond will be in touch about next steps.</span>}
            {external.status === "converted" && <span className="text-emerald-800 font-bold">Congratulations — you are now a Qualified Development Operator.</span>}
          </p>
        </div>
      )}

      <div className="bg-elevated border border-steel rounded-md p-4 flex items-start gap-3" data-testid="camp-help">
        <AlertTriangle className="h-4 w-4 text-lomond shrink-0 mt-0.5" />
        <p className="text-xs text-slate-500 leading-relaxed">
          Each day: read the theory and mark it complete, pass the daily quiz, complete the practical tasks with your instructor,
          write your reflection, and get signed off. If you fail a quiz you can revisit the learning and retry — mistakes are how operators learn.
        </p>
      </div>
    </div>
  );
}
